import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { LogmergeApiService, PreflightReport } from './logmerge-api.service';
import { SessionLoggerService } from './session-logger.service';

export interface ProcessingConfig {
  source_folder: string;
  log_suite_folder: string;
  min_step: number;
  null_value: string;
  filter_mnemonic: string;
  debug: boolean;
  allow_errors: boolean;
  allow_flagged: boolean;
  log_suite: boolean;
  clean: boolean;
  resample: boolean;
  combine: boolean;
  merge: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProcessingStateService {
  // Configuration
  config: ProcessingConfig = {
    source_folder: '',
    log_suite_folder: 'Modules/Utilities/Log Utilities/log_suite_s.ini',
    min_step: 0.1250,
    null_value: '-999.2500',
    filter_mnemonic: '',
    debug: true,
    allow_errors: true,
    allow_flagged: true,
    log_suite: false,
    clean: true,
    resample: true,
    combine: true,
    merge: true
  };

  // File upload
  selectedFiles: File[] = [];
  selectedLogSuiteFile: File | null = null;
  stagedUploadJobId: string | null = null;

  // Preflight state
  isPreflighting = false;
  preflightReport: PreflightReport | null = null;
  showScraperModal = false;
  scraperConfirmed = false;

  // Processing state
  isProcessing = false;
  progress = 0;
  statusMessage = '';
  jobId: string | null = null;

  // Download state -- true while a ZIP download is in flight (button
  // shows "Downloading..." and is disabled to prevent double-clicks).
  isDownloading = false;

  // Direct-to-Blob upload progress. Bytes go from the browser straight to
  // Azure Blob Storage, so unlike the old multipart POST we get real
  // byte-level progress instead of a single opaque request.
  isUploading = false;
  uploadPercent = 0;
  uploadedFileCount = 0;
  uploadTotalFileCount = 0;
  private uploadSubscription: Subscription | null = null;

  private smoothProgressTimer: ReturnType<typeof setInterval> | null = null;
  private processingStartedAt = 0;
  private lastLoggedStage = '';
  private pollSubscription: Subscription | null = null;

  constructor(
    private apiService: LogmergeApiService,
    private sessionLogger: SessionLoggerService
  ) {}

  // ---------------------------------------------------------------------------
  // File selection
  // ---------------------------------------------------------------------------

  setSelectedFiles(files: File[], source: 'browse' | 'drop'): void {
    // Fully reset session state so a fresh browse/drop always starts
    // clean -- even when the user re-selects the exact same LAS files
    // after a previous run. Without this, orphan polling timers, a
    // stale ``jobId``, and lingering preflight state would make the new
    // upload appear as a no-op or replay the previous session's UI.
    this.stopSmoothProgress();
    this.stopPolling();
    // Abandon any upload still streaming from a previous selection, otherwise
    // its progress callbacks would keep overwriting statusMessage.
    this.uploadSubscription?.unsubscribe();
    this.uploadSubscription = null;
    this.isUploading = false;
    this.uploadPercent = 0;
    this.uploadedFileCount = 0;
    this.uploadTotalFileCount = 0;

    this.selectedFiles = files;
    const verb = source === 'drop' ? 'dropped' : 'selected for upload';
    // Emit a single aggregated line instead of one per file. Big batches
    // (500+ LAS files) used to flood the System Logs view with "Selected
    // X.las" rows -- unusable in practice. First/last filenames give
    // enough context for spot-checks without drowning the log.
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    const totalMb = (totalBytes / (1024 * 1024)).toFixed(1);
    if (files.length === 0) {
      this.sessionLogger.info('File Upload', `0 file(s) ${verb}`);
    } else if (files.length === 1) {
      const only = files[0];
      const sizeKb = (only.size / 1024).toFixed(1);
      this.sessionLogger.info(
        'File Upload',
        `${source === 'drop' ? 'Dropped' : 'Selected'}: ${only.name} (${sizeKb} KB)`,
        only.name
      );
    } else {
      const first = files[0].name;
      const last = files[files.length - 1].name;
      this.sessionLogger.info(
        'File Upload',
        `${files.length} file(s) ${verb} (~${totalMb} MB): ${first} \u2192 ${last}`
      );
    }

    this.stagedUploadJobId = null;
    this.preflightReport = null;
    this.scraperConfirmed = false;
    this.showScraperModal = false;
    this.isProcessing = false;
    this.progress = 0;
    this.jobId = null;
    this.processingStartedAt = 0;
    this.lastLoggedStage = '';
    this.statusMessage = source === 'drop'
      ? `${files.length} LAS file(s) dropped`
      : `${files.length} file(s) selected`;

    if (files.length > 0) {
      this.runPreflight(true);
    }
  }

  setLogSuiteFile(file: File | null): void {
    if (file && file.name.toLowerCase().endsWith('.ini')) {
      this.selectedLogSuiteFile = file;
      this.config.log_suite_folder = file.name;
      this.statusMessage = `Selected log suite config: ${file.name}`;
    } else if (file) {
      this.selectedLogSuiteFile = null;
      this.config.log_suite_folder = '';
      this.statusMessage = 'Please select a valid .ini log suite config file.';
    }
  }

  // ---------------------------------------------------------------------------
  // Preflight (LAS Scraper)
  // ---------------------------------------------------------------------------

  runPreflight(showModalAfterSuccess: boolean = false): void {
    if (this.selectedFiles.length === 0 && !this.config.source_folder.trim()) {
      this.statusMessage = 'Please upload LAS files or specify a source folder for LAS Scraper.';
      return;
    }

    this.isPreflighting = true;
    this.preflightReport = null;
    this.sessionLogger.info('LAS Scraper', 'Starting LAS file validation...');

    if (this.config.source_folder.trim()) {
      this.statusMessage = 'Running LAS Scraper from source folder...';
      this.sessionLogger.info('LAS Scraper', `Scanning source folder: ${this.config.source_folder.trim()}`);
      this.apiService.runPreflightForPath(this.config.source_folder.trim()).subscribe({
        next: (response) => {
          this.preflightReport = response.report;
          this.statusMessage = `LAS Scraper complete: ${response.report.summary.total_files} files scanned.`;
          this.logPreflightSummary(response.report);
          this.showScraperModal = showModalAfterSuccess;
          this.isPreflighting = false;
        },
        error: (err) => {
          const detail = err.error?.detail || err.message;
          this.statusMessage = `LAS Scraper failed: ${detail}`;
          this.sessionLogger.error('LAS Scraper', `Validation failed: ${detail}`);
          this.isPreflighting = false;
        }
      });
      return;
    }

    const analyzeUploaded = (jobId: string): void => {
      this.apiService.runPreflightForUpload(jobId).subscribe({
        next: (response) => {
          this.preflightReport = response.report;
          this.statusMessage = `LAS Scraper complete: ${response.report.summary.total_files} files scanned.`;
          this.logPreflightSummary(response.report);
          this.showScraperModal = showModalAfterSuccess;
          this.isPreflighting = false;
        },
        error: (err) => {
          const detail = err.error?.detail || err.message;
          this.statusMessage = `LAS Scraper failed: ${detail}`;
          this.sessionLogger.error('LAS Scraper', `Validation failed: ${detail}`);
          this.isPreflighting = false;
        }
      });
    };

    if (this.stagedUploadJobId) {
      this.statusMessage = 'Running LAS Scraper...';
      analyzeUploaded(this.stagedUploadJobId);
      return;
    }

    this.uploadSelectedFilesToBlob(
      (jobId) => {
        this.stagedUploadJobId = jobId;
        analyzeUploaded(jobId);
      },
      (message) => {
        this.statusMessage = `LAS Scraper upload failed: ${message}`;
        this.isPreflighting = false;
      }
    );
  }

  /**
   * Upload the selected files straight to Blob Storage.
   *
   * Flow: create the job row -> request one write-only SAS URL per file ->
   * PUT the bytes directly to Blob (bounded concurrency) -> ask the server to
   * verify what landed. File bytes never pass through the API, which is what
   * lets large batches run without exhausting API memory.
   */
  private uploadSelectedFilesToBlob(
    onSuccess: (jobId: string) => void,
    onError: (message: string) => void
  ): void {
    const files = this.selectedFiles;
    const errorText = (err: any): string => err?.error?.detail || err?.message || 'Unknown error';

    this.isUploading = true;
    this.uploadPercent = 0;
    this.uploadedFileCount = 0;
    this.uploadTotalFileCount = files.length;
    this.statusMessage = `Preparing upload of ${files.length} file(s)...`;
    this.sessionLogger.info('File Upload', `Uploading ${files.length} file(s) directly to cloud storage...`);

    const fail = (stage: string, err: any): void => {
      this.isUploading = false;
      const detail = errorText(err);
      this.sessionLogger.error('File Upload', `${stage} failed: ${detail}`);
      onError(detail);
    };

    this.apiService.createJob(`LogMerge ${files.length} file(s)`).subscribe({
      next: (job) => {
        const jobId = job.job_id;

        this.apiService.getUploadUrls(jobId, files).subscribe({
          next: (urls) => {
            this.uploadSubscription?.unsubscribe();
            this.uploadSubscription = this.apiService
              .uploadFilesToBlob(files, urls.files)
              .subscribe({
                next: (p) => {
                  this.uploadPercent = p.percent;
                  this.uploadedFileCount = p.filesCompleted;
                  this.statusMessage =
                    `Uploading to cloud storage: ${p.filesCompleted}/${p.totalFiles} file(s) (${p.percent}%)`;
                },
                error: (err) => fail('Upload', err),
                complete: () => {
                  this.statusMessage = 'Verifying upload...';
                  this.apiService.confirmUpload(jobId, files).subscribe({
                    next: (result) => {
                      this.isUploading = false;
                      if (result.missing_files.length > 0) {
                        // Partial upload: surface exactly which files are
                        // missing rather than silently processing a subset.
                        const preview = result.missing_files.slice(0, 5).join(', ');
                        const more = result.missing_files.length > 5
                          ? ` (+${result.missing_files.length - 5} more)`
                          : '';
                        fail(
                          'Upload verification',
                          { message: `${result.missing_files.length} file(s) did not upload: ${preview}${more}` }
                        );
                        return;
                      }
                      this.uploadPercent = 100;
                      this.sessionLogger.info(
                        'File Upload',
                        `Upload complete: ${result.uploaded_count} file(s) (job ${jobId.substring(0, 8)})`
                      );
                      onSuccess(jobId);
                    },
                    error: (err) => fail('Upload verification', err)
                  });
                }
              });
          },
          error: (err) => fail('Requesting upload URLs', err)
        });
      },
      error: (err) => fail('Job creation', err)
    });
  }

  confirmScraperInsights(): void {
    this.scraperConfirmed = true;
    this.showScraperModal = false;
    this.statusMessage = 'LAS Scraper validated. You can now start processing.';
    this.sessionLogger.info('LAS Scraper', 'Validation insights confirmed - ready for processing');
  }

  cancelScraperInsights(): void {
    this.showScraperModal = false;
    this.scraperConfirmed = false;
    this.preflightReport = null;
    this.stagedUploadJobId = null;
    this.selectedFiles = [];
    this.statusMessage = 'Validation cancelled. Please upload LAS files again.';
  }

  // ---------------------------------------------------------------------------
  // Processing
  // ---------------------------------------------------------------------------

  get isStartProcessingDisabled(): boolean {
    if (this.selectedFiles.length === 0 && !this.config.source_folder.trim()) {
      return true;
    }
    if (this.selectedFiles.length > 0 && !this.scraperConfirmed) {
      return true;
    }
    if (this.isProcessing) {
      return true;
    }
    if (this.progress === 100) {
      return true;
    }
    return false;
  }

  startProcessing(): void {
    if (this.selectedFiles.length === 0 && !this.config.source_folder) {
      this.statusMessage = 'Please upload LAS files or specify a source folder.';
      return;
    }

    if (this.selectedFiles.length > 0 && !this.scraperConfirmed) {
      this.statusMessage = 'Please review LAS Scraper insights and click Confirm before processing.';
      this.showScraperModal = true;
      return;
    }

    this.sessionLogger.info('Merge Process', 'Starting log merge processing');
    if (this.selectedFiles.length > 0) {
      this.sessionLogger.info('Merge Process', `Processing ${this.selectedFiles.length} uploaded file(s)`);
    }
    if (this.config.source_folder.trim()) {
      this.sessionLogger.info('Merge Process', `Source folder: ${this.config.source_folder.trim()}`);
    }
    const enabledStages = [
      this.config.clean ? 'Clean' : null,
      this.config.resample ? 'Resample' : null,
      this.config.combine ? 'Combine' : null,
      this.config.merge ? 'Merge' : null
    ].filter(Boolean).join(', ');
    if (enabledStages) {
      this.sessionLogger.info('Merge Process', `Enabled stages: ${enabledStages}`);
    }

    this.isProcessing = true;
    this.progress = 0;
    this.processingStartedAt = Date.now();
    this.lastLoggedStage = '';
    this.startSmoothProgress();

    const launchProcessing = (resolvedConfig: any): void => {
      if (this.config.source_folder.trim()) {
        this.statusMessage = 'Starting processing from folder path...';
        this.progress = 10;

        this.apiService.startProcessingFromPath(resolvedConfig).subscribe({
          next: (response) => {
            this.jobId = response.job_id;
            this.statusMessage = 'Processing...';
            this.progress = 20;
            this.pollStatus();
          },
          error: (err) => {
            this.stopSmoothProgress();
            const detail = err.error?.detail || err.message;
            this.statusMessage = `Error: ${detail}`;
          }
        });
        return;
      }

      const startUploadedJob = (uploadedJobId: string): void => {
        this.jobId = uploadedJobId;
        this.stagedUploadJobId = null;
        this.statusMessage = 'Files uploaded. Creating chunks...';
        this.progress = 20;

        const failJob = (prefix: string) => (err: any) => {
          this.stopSmoothProgress();
          this.isProcessing = false;
          const detail = err.error?.detail || err.message;
          this.statusMessage = `${prefix}: ${detail}`;
        };

        // Chunking must finish before dispatch - the backend rejects a
        // dispatch for a job that has no chunk rows yet.
        this.apiService.createChunks(uploadedJobId).subscribe({
          next: (chunkRes) => {
            this.statusMessage =
              `Created ${chunkRes.total_chunks} chunk(s) from ${chunkRes.total_files} file(s). Dispatching...`;
            this.progress = 25;

            this.apiService.dispatchJob(uploadedJobId, resolvedConfig).subscribe({
              next: (res) => {
                // Work now runs in worker containers, so every further
                // progress update comes from polling, not from this response.
                this.statusMessage = res.message || 'Queued for processing...';
                this.pollStatus();
              },
              error: failJob('Dispatch failed')
            });
          },
          error: failJob('Chunking failed')
        });
      };

      if (this.stagedUploadJobId) {
        this.statusMessage = 'Starting processing with LAS Scraper upload...';
        startUploadedJob(this.stagedUploadJobId);
        return;
      }

      this.uploadSelectedFilesToBlob(
        (jobId) => startUploadedJob(jobId),
        (message) => {
          this.stopSmoothProgress();
          this.isProcessing = false;
          this.statusMessage = `Upload failed: ${message}`;
        }
      );
    };

    const resolvedConfig = { ...this.config };

    if (this.selectedLogSuiteFile) {
      this.statusMessage = 'Uploading log suite config...';
      this.apiService.uploadLogSuiteConfig(this.selectedLogSuiteFile).subscribe({
        next: (res) => {
          resolvedConfig.log_suite_folder = res.path;
          launchProcessing(resolvedConfig);
        },
        error: (err) => {
          this.stopSmoothProgress();
          this.isProcessing = false;
          const detail = err.error?.detail || err.message;
          this.statusMessage = `Config upload failed: ${detail}`;
        }
      });
      return;
    }

    launchProcessing(resolvedConfig);
  }

  cancel(): void {
    if (!this.jobId || !this.isProcessing) {
      this.stopSmoothProgress();
      this.stopPolling();
      this.isProcessing = false;
      this.progress = 0;
      this.statusMessage = 'Processing cancelled.';
      this.selectedFiles = [];
      return;
    }

    this.statusMessage = 'Cancelling processing...';
    this.apiService.cancelProcessing(this.jobId).subscribe({
      next: (response) => {
        this.stopSmoothProgress();
        this.stopPolling();
        this.isProcessing = false;
        this.progress = 0;
        this.statusMessage = response.message;
        this.selectedFiles = [];
        this.stagedUploadJobId = null;
        this.scraperConfirmed = false;
      },
      error: (err) => {
        this.stopSmoothProgress();
        this.stopPolling();
        this.isProcessing = false;
        const detail = err.error?.detail || err.message;
        this.statusMessage = `Cancel failed: ${detail}`;
      }
    });
  }

  downloadResults(): void {
    if (!this.jobId) return;
    if (this.isDownloading) return; // guard against double-click / re-entry
    this.isDownloading = true;
    this.downloadResultsWithRetry(0);
  }

  /**
   * Fetch the results ZIP, retrying while the backend is still packaging
   * (HTTP 202). Uses exponential backoff capped at 15 s and gives up after
   * ~5 minutes so a genuinely stuck job surfaces an error to the user.
   *
   * Cloud jobs are served by redirecting the browser at a SAS URL so the
   * archive never streams through the API, which would exceed App Service's
   * 230 s idle timeout on large runs. Legacy local jobs have no SAS, so a
   * failure here falls back to streaming from the API.
   */
  private downloadResultsWithRetry(attempt: number): void {
    if (!this.jobId) {
      this.isDownloading = false;
      return;
    }

    const jobId = this.jobId;
    this.statusMessage = attempt === 0
      ? 'Preparing download...'
      : 'Results archive is still being packaged, retrying...';

    this.apiService.getResultsDownloadUrl(jobId).subscribe({
      next: (response) => {
        if (response.status === 202) {
          this.scheduleDownloadRetry(
            attempt,
            response.headers.get('Retry-After'),
            () => this.downloadResultsWithRetry(attempt + 1)
          );
          return;
        }

        const url = response.body?.download_url;
        if (!url) {
          this.downloadResultsViaApi(attempt);
          return;
        }

        this.triggerBrowserDownload(
          url,
          response.body?.filename || `logmerge_results_${jobId}.zip`
        );
        this.finishDownload();
      },
      error: () => {
        // No SAS available - either a legacy local job with no SQL row, or
        // Blob/SQL is unconfigured. Stream through the API instead.
        this.downloadResultsViaApi(attempt);
      }
    });
  }

  /** Legacy path: stream the ZIP through the API as a Blob. */
  private downloadResultsViaApi(attempt: number): void {
    if (!this.jobId) {
      this.isDownloading = false;
      return;
    }

    const jobId = this.jobId;

    this.apiService.downloadResults(jobId).subscribe({
      next: (response) => {
        // 202 Accepted -> archive still being built, poll again.
        if (response.status === 202) {
          this.scheduleDownloadRetry(
            attempt,
            response.headers.get('Retry-After'),
            () => this.downloadResultsViaApi(attempt + 1)
          );
          return;
        }

        const blob = response.body;
        if (!blob) {
          this.statusMessage = 'Error downloading results (empty response)';
          this.isDownloading = false;
          return;
        }

        const url = window.URL.createObjectURL(blob);
        this.triggerBrowserDownload(url, `logmerge_results_${jobId}.zip`);
        window.URL.revokeObjectURL(url);
        this.finishDownload();
      },
      error: () => {
        this.statusMessage = 'Error downloading results';
        this.isDownloading = false;
      }
    });
  }

  /**
   * Schedule the next download attempt, or give up once the ~5 minute
   * budget (roughly 30 attempts at capped backoff) is spent.
   */
  private scheduleDownloadRetry(
    attempt: number,
    retryAfterHeader: string | null,
    next: () => void
  ): void {
    if (attempt >= 30) {
      this.statusMessage = 'Results archive is taking longer than expected. Please try again shortly.';
      this.isDownloading = false;
      return;
    }

    const retryAfterSec = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
    // Exponential backoff with a floor from Retry-After and a 15 s cap.
    const backoffSec = Math.min(15, Math.max(
      isFinite(retryAfterSec) ? retryAfterSec : 2,
      Math.pow(2, Math.min(attempt, 4))
    ));

    setTimeout(next, backoffSec * 1000);
  }

  /**
   * Kick off a browser download.
   *
   * Note: for a cross-origin SAS URL the browser ignores `download` and names
   * the file after the blob - which is already `results_<job_id>.zip`.
   */
  private triggerBrowserDownload(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  }

  private finishDownload(): void {
    this.statusMessage = 'Download complete. Ready for a new run.';
    // Short delay so the browser's download prompt lands before the
    // page snaps back to its empty state.
    setTimeout(() => {
      this.isDownloading = false;
      this.resetForNextRun();
    }, 1200);
  }

  /**
   * Reset the app back to a clean "ready for a new upload" state after
   * a successful download. Deliberately does NOT clear the config so
   * the user can re-run the same pipeline with new files, and does NOT
   * touch the backend job store (that survives for support/debug).
   */
  private resetForNextRun(): void {
    this.stopSmoothProgress();
    this.stopPolling();
    this.jobId = null;
    this.progress = 0;
    this.isProcessing = false;
    this.selectedFiles = [];
    this.selectedLogSuiteFile = null;
    this.stagedUploadJobId = null;
    this.preflightReport = null;
    this.showScraperModal = false;
    this.isPreflighting = false;
    this.scraperConfirmed = false;
    this.statusMessage = 'Ready. Select new LAS files to start another run.';
  }

  // ---------------------------------------------------------------------------
  // Polling & progress
  // ---------------------------------------------------------------------------

  private pollStatus(): void {
    if (!this.jobId) return;
    this.stopPolling();

    this.pollSubscription = this.apiService.pollStatus(this.jobId).subscribe({
      next: (status) => {
        this.progress = Math.max(this.progress, status.progress);

        if (status.status === 'completed') {
          this.stopSmoothProgress();
          this.progress = 100;
          this.isProcessing = false;
          this.statusMessage = 'Processing completed successfully!';
          this.sessionLogger.info('Merge Process', 'Log merge processing completed successfully');
        } else if (status.status === 'completed_with_errors') {
          // Terminal and downloadable: results exist for the chunks that
          // succeeded. Without this branch the job never clears isProcessing.
          this.stopSmoothProgress();
          this.progress = 100;
          this.isProcessing = false;
          this.statusMessage = status.message || 'Completed with some failed chunks.';
          this.sessionLogger.info(
            'Merge Process',
            status.message || 'Log merge completed with some failed chunks'
          );
        } else if (status.status === 'cancelled') {
          this.stopSmoothProgress();
          this.isProcessing = false;
          this.progress = 0;
          this.statusMessage = status.message || 'Processing cancelled.';
        } else if (status.status === 'failed') {
          this.stopSmoothProgress();
          this.isProcessing = false;
          this.statusMessage = `Processing failed: ${status.error}`;
        } else if (status.message && !this.isGenericProcessingMessage(status.message)) {
          this.statusMessage = status.message;
        }
      },
      error: () => {
        this.stopSmoothProgress();
        this.isProcessing = false;
        this.statusMessage = 'Error checking status';
      }
    });
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  private startSmoothProgress(): void {
    this.stopSmoothProgress();

    this.smoothProgressTimer = setInterval(() => {
      if (!this.isProcessing) {
        this.stopSmoothProgress();
        return;
      }

      if (this.progress < 92) {
        this.progress += 1;
      }

      const stageMsg = this.getStageMessage();
      this.statusMessage = stageMsg;
      this.logStageTransition(stageMsg);
    }, 1200);
  }

  private stopSmoothProgress(): void {
    if (this.smoothProgressTimer) {
      clearInterval(this.smoothProgressTimer);
      this.smoothProgressTimer = null;
    }
  }

  private getStageMessage(): string {
    const elapsedSec = (Date.now() - this.processingStartedAt) / 1000;

    // Build the actual enabled-stage list so the message reflects what
    // the user asked LogMerge to run. Previously this was hardcoded to
    // Clean -> Resample -> Combine -> Merge and always displayed those
    // labels even when the user disabled some stages.
    const enabledStages: { key: string; message: string }[] = [];
    if (this.config.clean) {
      enabledStages.push({ key: 'clean', message: 'Cleaning and standardizing logs...' });
    }
    if (this.config.resample) {
      enabledStages.push({ key: 'resample', message: 'Resampling and aligning depth intervals...' });
    }
    if (this.config.combine) {
      enabledStages.push({ key: 'combine', message: 'Combining curves and resolving overlap...' });
    }
    if (this.config.merge) {
      enabledStages.push({ key: 'merge', message: 'Merging mnemonic families into final LAS...' });
    }

    // Initial validation / upload band -- shown before any stage starts.
    if (this.progress < 20 || elapsedSec < 4) {
      return 'Validating LAS files and preparing job...';
    }

    // Edge case: user unchecked every stage. Nothing to run past setup.
    if (enabledStages.length === 0) {
      return 'Preparing outputs...';
    }

    // Finalize band -- name the terminal stage the user actually ran so
    // the message doesn't say "merged LAS" when merge wasn't selected.
    if (this.progress >= 92) {
      const last = enabledStages[enabledStages.length - 1];
      return `Finalizing ${this.finalizeNoun(last.key)}...`;
    }

    // Divide the 20..92 progress band evenly among the enabled stages so
    // each active step gets a slot proportional to the selection size.
    const bandStart = 20;
    const bandEnd = 92;
    const slotWidth = (bandEnd - bandStart) / enabledStages.length;
    const idx = Math.min(
      enabledStages.length - 1,
      Math.max(0, Math.floor((this.progress - bandStart) / slotWidth))
    );
    return enabledStages[idx].message;
  }

  private finalizeNoun(stageKey: string): string {
    switch (stageKey) {
      case 'clean':    return 'cleaned LAS output';
      case 'resample': return 'resampled LAS output';
      case 'combine':  return 'combined LAS output';
      case 'merge':    return 'merged LAS output';
      default:         return 'output';
    }
  }

  private isGenericProcessingMessage(message: string): boolean {
    const normalized = message.trim().toLowerCase();
    return normalized === 'processing...' || normalized === 'running logmerge pipeline...';
  }

  private logStageTransition(stageMessage: string): void {
    if (!stageMessage || stageMessage === this.lastLoggedStage) {
      return;
    }
    this.lastLoggedStage = stageMessage;
    this.sessionLogger.info('Merge Process', `Stage: ${stageMessage}`);
  }

  private logPreflightSummary(report: PreflightReport): void {
    const s = report.summary;
    this.sessionLogger.info(
      'LAS Scraper',
      `Validation complete: ${s.total_files} files scanned, ${s.valid_files} valid, ${s.failed_files} failed`
    );
    this.sessionLogger.info(
      'LAS Scraper',
      `Detected ${s.wells_detected} well(s); ${s.wells_with_multiple_files} with multiple files, ${s.wells_with_overlaps} with overlaps`
    );
    this.sessionLogger.info(
      'LAS Scraper',
      `Curves: ${s.total_curves} total, ${s.unique_mnemonics} unique mnemonics, ${s.unit_conflict_mnemonics} unit conflicts`
    );
    if (s.files_missing_well_id > 0) {
      this.sessionLogger.warning(
        'LAS Scraper',
        `${s.files_missing_well_id} file(s) missing well ID (UWI)`
      );
    }
    if (s.files_missing_depth_headers > 0) {
      this.sessionLogger.warning(
        'LAS Scraper',
        `${s.files_missing_depth_headers} file(s) missing depth headers`
      );
    }
    (report.warnings || []).slice(0, 5).forEach(w => {
      this.sessionLogger.warning('LAS Scraper', w);
    });
  }
}
